/*
 * Copyright © HatioLab Inc. All rights reserved.
 */

import {
  Component,
  ComponentNature,
  Properties,
  RealObject,
  ServiceComponent,
  sceneComponent
} from '@hatiolab/things-scene'
import * as THREE from 'three'
import { Stock, type StockMaterialProvider } from './stock.js'

const STOCK_HUB_ICON_SRC =
  'data:image/svg+xml;base64,' +
  btoa(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" fill="#d09050">
  <rect x="8" y="28" width="18" height="28" rx="2" opacity="0.8"/>
  <rect x="23" y="18" width="18" height="38" rx="2" opacity="0.6"/>
  <rect x="38" y="8" width="18" height="48" rx="2" opacity="0.9"/>
  <rect x="4" y="56" width="56" height="4" rx="1"/>
</svg>`
  )

const NATURE: ComponentNature = {
  mutable: false,
  resizable: true,
  rotatable: true,
  properties: [
    {
      type: 'string',
      label: 'location-field',
      name: 'locationField',
      placeholder: 'location'
    },
    {
      type: 'id-input',
      label: 'legend-target',
      name: 'legendTarget',
      property: {
        component: 'legend'
      }
    },
    {
      type: 'checkbox',
      label: 'hide-empty-stock',
      name: 'hideEmptyStock'
    }
  ],
  help: 'scene/component/stock-hub'
}

@sceneComponent('stock-hub')
export class StockHub extends ServiceComponent implements StockMaterialProvider {
  static _icon?: HTMLImageElement

  static get icon(): HTMLImageElement {
    if (!StockHub._icon) {
      StockHub._icon = new Image()
      StockHub._icon.src = STOCK_HUB_ICON_SRC
    }
    return StockHub._icon
  }

  // === StockMaterialProvider ===
  _stock_materials: THREE.Material[] = []
  _default_material?: THREE.Material
  _empty_material?: THREE.Material
  private _legendTarget?: Component

  // === Stock 레지스트리 ===
  _objects: { [id: string]: RealObject } = {}

  get nature(): ComponentNature {
    return NATURE
  }

  get serviceName(): string {
    return 'stock'
  }

  get hideEmptyStock(): boolean {
    return !!this.getState('hideEmptyStock')
  }

  get legendTarget(): Component | undefined {
    const { legendTarget } = this.state

    if (!this._legendTarget && legendTarget) {
      this._legendTarget = this.root.findById?.(legendTarget)
      this._legendTarget?.on('change', this._onLegendChanged, this)
    }

    return this._legendTarget
  }

  /* === Stock 레지스트리 메서드 === */

  putObject(id: string, object: RealObject): void {
    id && (this._objects[id] = object)
  }

  getObject(id: string): RealObject {
    return this._objects[id]
  }

  /* === 라이프사이클 === */

  dispose(): void {
    this._legendTarget?.off('change', this._onLegendChanged, this)
    delete this._legendTarget
    this._resetMaterials()
    this._objects = {}

    super.dispose()
  }

  /* === 데이터 분배 === */

  onchangeData(): void {
    if (typeof this.data !== 'object') {
      return
    }

    let data = this.data as any
    const locationField = this.getState('locationField') || 'location'

    if (data instanceof Array) {
      data = data.reduce((acc: any, value: any) => {
        const location = value[locationField]

        if (!location) {
          return acc
        }

        if (acc[location]) {
          acc[location]['items'].push(value)
        } else {
          acc[location] = { items: [value] }
        }

        return acc
      }, {})
    }

    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        const d = data[key]
        const object = this.getObject(key)
        if (object) {
          ;(object as Stock).onchangeStockData(d)
        }
      }
    }
  }

  /* === 이벤트 핸들러 === */

  onchange(after: Properties, before: Properties): void {
    if ('legendTarget' in before || 'legendTarget' in after) {
      this._legendTarget?.off('change', this._onLegendChanged, this)
      delete this._legendTarget
      this._resetMaterials()
    }

    this.invalidate()
  }

  private _onLegendChanged(): void {
    this._resetMaterials()
    this.invalidate()
  }

  private _resetMaterials(): void {
    this._stock_materials.forEach(m => m.dispose?.())
    this._stock_materials = []
    delete this._default_material
    delete this._empty_material
  }
}
